import { DateTime } from 'luxon';
import { DateRange } from '../types/date-range';

enum PeriodType {
  SEMIMONTH,
  MONTH,
  QUARTER,
  DAYS
}

const getDaysDifference = (start: DateTime, end: DateTime): number => {
  return end.plus({ days: 1 }).diff(start, ['days']).toObject().days as number;
};

const determinePeriodType = (start: DateTime, end: DateTime): PeriodType => {
  const daysDifference = getDaysDifference(end, start);
  if ((start.day === 1 || start.day === 16) && daysDifference <= 16) {
    if (start.day === 1 && end.day === 15) {
      return PeriodType.SEMIMONTH;
    } else if (start.day === 16 && end.day === end.endOf('month').day)
      return PeriodType.SEMIMONTH;
  }
  if (start.day === 1) {
    const predictedMonthEndDate = start.plus({ months: 1 }).minus({ days: 1 });
    if (predictedMonthEndDate.ordinal == end.ordinal) return PeriodType.MONTH;

    const predictedQuarterEndDate = start
      .plus({ months: 3 })
      .minus({ days: 1 });
    if (predictedQuarterEndDate.ordinal == end.ordinal)
      return PeriodType.QUARTER;
  }
  return PeriodType.DAYS;
};

export const getNextPeriod = (start: DateTime, end: DateTime): DateRange => {
  start = start.set({ hour: 0, minute: 0, second: 0 });
  end = end.set({ hour: 0, minute: 0, second: 0 });

  const periodType = determinePeriodType(start, end);
  switch (periodType) {
    case PeriodType.SEMIMONTH: {
      let startDate = null;
      let endDate = null;
      if (start.day === 1) {
        startDate = start.plus({ days: 15 });
        endDate = start
          .endOf('month')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      } else {
        startDate = end.plus({ days: 1 });
        endDate = end.plus({ days: 15 });
      }
      return {
        start: startDate,
        end: endDate
      };
      break;
    }
    case PeriodType.MONTH: {
      const newMonth = start.plus({ months: 1 });
      return {
        start: newMonth.startOf('month'),
        end: newMonth
          .endOf('month')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      };
      break;
    }
    case PeriodType.QUARTER: {
      const newMonth = end.plus({ months: 3 });

      return {
        start: end.plus({ days: 1 }),
        end: newMonth
          .endOf('month')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      };
      break;
    }
    default: {
      const days = getDaysDifference(start, end);
      return {
        start: end.plus({ days: 1 }),
        end: end.plus({ days: days })
      };
      break;
    }
  }
};

export const getPreviousPeriod = (
  start: DateTime,
  end: DateTime
): DateRange => {
  start = start.set({ hour: 0, minute: 0, second: 0 });
  end = end.set({ hour: 0, minute: 0, second: 0 });

  const periodType = determinePeriodType(start, end);
  switch (periodType) {
    case PeriodType.SEMIMONTH: {
      let startDate = null;
      let endDate = null;
      if (start.day === 1) {
        const previousMonth = start.minus({ months: 1 });
        startDate = previousMonth.startOf('month').plus({ days: 15 });
        endDate = previousMonth
          .endOf('month')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      } else {
        startDate = start.startOf('month');
        endDate = start.minus({ days: 1 });
      }
      return {
        start: startDate,
        end: endDate
      };
      break;
    }
    case PeriodType.MONTH: {
      const newMonth = start.minus({ months: 1 });
      return {
        start: newMonth.startOf('month'),
        end: newMonth
          .endOf('month')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      };
      break;
    }
    case PeriodType.QUARTER: {
      const newMonth = start.minus({ months: 3 });
      return {
        start: newMonth.startOf('month'),
        end: start.minus({ days: 1 })
      };
      break;
    }
    default: {
      const days = getDaysDifference(start, end);
      return {
        start: start.minus({ days: Math.floor(days) }),
        end: start.minus({ days: 1 })
      };
      break;
    }
  }
};

export const getRangeHonoringWeekStartDay = ({
  weekStart,
  date = DateTime.local()
}: {
  weekStart: number;
  date?: DateTime;
}): DateRange => {
  const start = date
    .startOf('week')
    .minus({ days: 1 })
    .minus({ days: date.weekday >= weekStart ? 0 : 7 })
    .plus({ days: date.weekday === 7 && weekStart === 0 ? 7 : weekStart }); // bug fix: if the week is sunday and week start is also sunday, it goes out of range between start and end date

  const end = start.plus({ days: 6 });

  return { start, end };
};
